<div class="bg-white rounded-2xl overflow-hidden w-full mx-auto flex flex-col" [ngClass]="shellClasses">

    @if(mode === 'view' && taskToEdit) {
    <!-- ============================ VIEW MODE (Autotask-style 3 columns) ============================ -->
    @let status = getStatusInfo();
    @let priority = getPriorityInfo();
    @let assigned = getAssignedEmployees();
    @let isDone = taskData.status === 'DONE';

    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
        <div class="flex items-center gap-3 min-w-0">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
            </div>
            <div class="min-w-0">
                <h2 class="font-bold text-lg text-gray-900 leading-tight truncate">{{ taskData.nameTask }}</h2>
                <p class="text-xs" [ngClass]="isTaskDone ? 'text-emerald-600 font-medium' : 'text-gray-400'">
                    @if(isTaskDone) { Completed — locked for editing } @else { Task details }
                </p>
            </div>
        </div>
        <div class="flex items-center gap-2 shrink-0">
            @if(isTaskDone) {
            <!-- A completed task is read-only: reopening it is the only way back in. -->
            <button type="button" (click)="reopenTask()" [disabled]="isReopening"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">
                <svg class="w-4 h-4" [class.animate-spin]="isReopening" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                @if(isReopening) { Reopening... } @else { Reopen }
            </button>
            } @else {
            <button type="button" (click)="switchToEdit()"
                class="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
                Edit
            </button>
            }
            <button type="button" (click)="copyTaskContent()"
                [title]="copied ? 'Copied' : 'Copy description and checklist'"
                class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-sm font-medium rounded-lg transition-colors cursor-pointer"
                [ngClass]="copied ? 'text-emerald-700 bg-emerald-50' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'">
                @if(copied) {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
                Copied
                } @else {
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                </svg>
                Copy
                }
            </button>
            @if(canUserAccessTask()) {
            <button type="button" (click)="deleteTask()" title="Delete task"
                class="p-1.5 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors cursor-pointer">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
            </button>
            }
            <button (click)="close.emit()" class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>
        </div>
    </div>

    <!-- Body: context rail | details | conversation & history -->
    <div class="flex flex-1 min-h-0 divide-x divide-gray-100">

        <!-- ── Left rail: where the task lives and who owns it ── -->
        <aside class="w-72 shrink-0 bg-gray-50/70 overflow-y-auto custom-scrollbar p-4 space-y-5">

            <div>
                <div class="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-2">Context</div>
                <dl class="space-y-3">
                    <div>
                        <dt class="flex items-center gap-1.5 text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-0.5">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
                            Project
                        </dt>
                        <dd class="text-sm font-medium text-gray-800 break-words">{{ taskContext.projectName || '—' }}</dd>
                    </div>
                    <div>
                        <dt class="flex items-center gap-1.5 text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-0.5">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 10h16M4 14h10" /></svg>
                            Phase
                        </dt>
                        <dd class="text-sm font-medium text-gray-800 break-words">{{ taskContext.stageName || '—' }}</dd>
                    </div>
                    <div>
                        <dt class="flex items-center gap-1.5 text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-0.5">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                            Client
                        </dt>
                        <dd class="text-sm font-medium text-gray-800 break-words">{{ taskContext.clientName || '—' }}</dd>
                    </div>
                </dl>
            </div>

            <div class="border-t border-gray-200 pt-4">
                <div class="text-[11px] text-gray-400 font-bold uppercase tracking-wider mb-2">
                    Assigned to @if(assigned.length) { <span class="text-blue-600">({{ assigned.length }})</span> }
                </div>
                @if(assigned.length > 0) {
                <ul class="space-y-2">
                    @for(emp of assigned; track emp.id) {
                    <li class="flex items-center gap-2.5">
                        <span class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold shrink-0">{{ getInitials(emp) }}</span>
                        <div class="min-w-0">
                            <div class="text-sm font-medium text-gray-800 truncate">{{ emp.firstName }} {{ emp.name }}</div>
                            @if(emp.email) { <div class="text-[11px] text-gray-400 truncate">{{ emp.email }}</div> }
                        </div>
                    </li>
                    }
                </ul>
                } @else {
                <p class="text-sm text-gray-400 italic">No one assigned</p>
                }
            </div>

            <div class="border-t border-gray-200 pt-4 space-y-3">
                <div>
                    <div class="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-1">Status</div>
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border" [ngClass]="status.classes">{{ status.label }}</span>
                </div>
                <div>
                    <div class="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-1">Priority</div>
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border" [ngClass]="priority.classes">
                        <span class="w-2 h-2 rounded-full" [ngClass]="priority.dot"></span> {{ priority.label }}
                    </span>
                </div>
            </div>

            @if(validatedByEmployee) {
            <div class="border-t border-gray-200 pt-4">
                <div class="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-1">Validated</div>
                <div class="flex items-center gap-2">
                    <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                    <span class="text-sm text-gray-700 min-w-0 truncate">
                        {{ validatedByEmployee.firstName }} {{ validatedByEmployee.name }}@if(taskToEdit?.validatedAt) { · {{ taskToEdit!.validatedAt | date:'dd/MM/yy' }} }
                    </span>
                </div>
            </div>
            }
        </aside>

        <!-- ── Centre: the task itself ── -->
        <div class="flex-1 min-w-0 overflow-y-auto custom-scrollbar px-6 py-5 space-y-5 bg-gray-50/40">

            <!-- Hero card -->
            <div class="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                <div class="h-1.5 w-full" [ngClass]="statusAccent"></div>
                <div class="p-5 space-y-3">
                    @if(isOverdue) {
                    <span class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border border-red-200 bg-red-50 text-red-700">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /></svg>
                        Overdue
                    </span>
                    }
                    <h3 class="text-2xl font-bold text-gray-900 leading-snug break-words" [class.line-through]="isDone" [class.text-gray-400]="isDone">{{ taskData.nameTask }}</h3>
                </div>
            </div>

            <!-- Description -->
            <div class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wide">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h7" /></svg>
                    Description
                </div>
                @if(taskData.content) {
                <div class="text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none" [innerHTML]="taskData.content"></div>
                } @else {
                <p class="text-sm text-gray-400 italic">No description added</p>
                }
            </div>

            <!-- Checklist -->
            <div class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="flex items-center justify-between gap-2 mb-2">
                    <div class="flex items-center gap-1.5 text-xs text-gray-500 font-semibold uppercase tracking-wide">
                        <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                        Checklist
                        @if(checklist.length) {
                        <span class="text-blue-600 normal-case">({{ checklistDoneCount }}/{{ checklist.length }})</span>
                        }
                    </div>
                    <div class="flex items-center gap-2 shrink-0 relative">
                        <button type="button" (click)="openTemplateMenu()"
                            class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 cursor-pointer">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                            Templates
                        </button>
                        @if(!isTaskDone) {
                        <button type="button" (click)="toggleChecklistAdd()"
                            class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                            Add
                        </button>
                        }

                        @if(showTemplateMenu) {
                        <div class="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                            <div class="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
                                Apply a template
                            </div>
                            <div class="max-h-52 overflow-y-auto custom-scrollbar">
                                @for(tpl of checklistTemplates; track tpl.id) {
                                <div (click)="applyTemplate(tpl)"
                                    class="group flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                    <div class="min-w-0 flex-1">
                                        <div class="text-sm text-gray-800 truncate">{{ tpl.name }}</div>
                                        <div class="text-[11px] text-gray-400">{{ tpl.items.length }} items</div>
                                    </div>
                                    <button type="button" (click)="deleteTemplate(tpl, $event)" title="Delete template"
                                        class="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer shrink-0">
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                }
                                @empty {
                                <p class="px-3 py-4 text-xs text-gray-400 text-center">No template yet</p>
                                }
                            </div>

                            <div class="border-t border-gray-100 bg-gray-50/70 p-2">
                                @if(newTemplateName === null) {
                                <button type="button" (click)="startSavingTemplate()" [disabled]="!checklist.length"
                                    class="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                                    Save this checklist as a template
                                </button>
                                } @else {
                                <div class="flex items-center gap-1.5">
                                    <input type="text" [(ngModel)]="newTemplateName" [ngModelOptions]="{ standalone: true }"
                                        placeholder="Template name" (keydown.enter)="$event.preventDefault(); saveChecklistAsTemplate()"
                                        class="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400/50" />
                                    <button type="button" (click)="saveChecklistAsTemplate()"
                                        [disabled]="!newTemplateName || savingTemplate"
                                        class="px-2.5 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer shrink-0">Save</button>
                                </div>
                                }
                            </div>
                        </div>
                        }
                    </div>
                </div>

                @if(checklist.length) {
                <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-3">
                    <div class="h-full bg-emerald-500 transition-all duration-300" [style.width.%]="checklistPercent"></div>
                </div>
                }

                @if(showChecklistAdd && !isTaskDone) {
                <div class="flex items-center gap-2 mb-2">
                    <input type="text" [(ngModel)]="newChecklistLabel" [ngModelOptions]="{ standalone: true }"
                        placeholder="What needs doing?" (keydown.enter)="$event.preventDefault(); addChecklistItem()"
                        (keydown.escape)="toggleChecklistAdd()"
                        class="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400/50" />
                    <button type="button" (click)="addChecklistItem()" [disabled]="!newChecklistLabel.trim()"
                        class="px-2.5 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer shrink-0">Add</button>
                </div>
                }

                @if(checklistError) {
                <div class="flex items-start gap-2 mb-2 p-2.5 rounded-lg border border-amber-200 bg-amber-50">
                    <svg class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                    </svg>
                    <p class="text-xs text-amber-800 min-w-0 break-words flex-1">{{ checklistError }}</p>
                    <button type="button" (click)="checklistService.clearError()"
                        class="p-0.5 text-amber-600 hover:bg-amber-100 rounded cursor-pointer shrink-0">
                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                </div>
                }

                @if(loadingChecklist) {
                <p class="text-sm text-gray-400 italic">Loading checklist…</p>
                } @else if(checklist.length) {
                <ul class="space-y-1">
                    @for(item of checklist; track item.id) {
                    <li class="group flex items-start gap-2.5 py-1">
                        <button type="button" (click)="toggleChecklistItem(item)" [disabled]="isTaskDone"
                            class="shrink-0 mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors"
                            [class.cursor-pointer]="!isTaskDone" [class.cursor-not-allowed]="isTaskDone"
                            [class.bg-emerald-500]="item.done" [class.border-emerald-500]="item.done"
                            [class.border-gray-300]="!item.done" [class.bg-white]="!item.done">
                            @if(item.done) {
                            <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                            }
                        </button>
                        <span class="flex-1 min-w-0 text-sm break-words" [class.line-through]="item.done"
                            [class.text-gray-400]="item.done" [class.text-gray-700]="!item.done">{{ item.label }}</span>
                        @if(!isTaskDone) {
                        <button type="button" (click)="removeChecklistItem(item)"
                            class="opacity-0 group-hover:opacity-100 p-0.5 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer shrink-0">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                        }
                    </li>
                    }
                </ul>
                } @else {
                <p class="text-sm text-gray-400 italic">No checklist — add an item or apply a template.</p>
                }
            </div>

            <!-- Links -->
            <div class="rounded-xl border border-gray-200 bg-white p-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wide">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m4.5-4.5l1.5-1.5a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656 0" /></svg>
                    Links @if(taskData.links.length) { <span class="text-blue-600 normal-case">({{ taskData.links.length }})</span> }
                </div>
                @if(taskData.links.length > 0) {
                <div class="flex flex-wrap gap-1.5">
                    @for(link of taskData.links; track $index) {
                    <a [href]="link.url" (click)="openExternalLink(link.url, $event)" target="_blank" rel="noopener noreferrer"
                        class="inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md hover:border-blue-300 hover:bg-blue-50 transition-all text-xs text-blue-600 hover:underline cursor-pointer">
                        @if(isDriveLink(link)) {
                        <svg class="w-3 h-3 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 5.5L11.14 9zM9.4 15l-3.28 5.5h11.76L21.16 15zm12.02-1.5L15.28 3.5H8.42l6.14 10z" /></svg>
                        } @else {
                        <svg class="w-3 h-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 010 5.656l-3 3a4 4 0 01-5.656-5.656l1.5-1.5m4.5-4.5l1.5-1.5a4 4 0 015.656 5.656l-3 3a4 4 0 01-5.656 0" /></svg>
                        }
                        <span class="truncate max-w-[180px]">{{ link.name }}</span>
                    </a>
                    }
                </div>
                } @else {
                <p class="text-sm text-gray-400 italic">No link added</p>
                }
            </div>

            <!-- Schedule -->
            <div>
                <div class="flex items-center gap-1.5 text-xs text-gray-500 font-semibold mb-2 uppercase tracking-wide">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Schedule
                </div>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div class="rounded-xl border border-gray-200 bg-white p-3">
                        <div class="text-xs text-gray-500 font-semibold mb-1">Start</div>
                        <div class="text-sm font-medium text-gray-800">{{ startDateValue ? (startDateValue | date:'dd/MM/yyyy') : '—' }}</div>
                    </div>
                    <div class="rounded-xl border bg-white p-3" [ngClass]="isOverdue ? 'border-red-200 bg-red-50/60' : 'border-gray-200'">
                        <div class="text-xs font-semibold mb-1" [ngClass]="isOverdue ? 'text-red-600' : 'text-gray-500'">Due</div>
                        <div class="text-sm font-medium" [ngClass]="isOverdue ? 'text-red-700' : 'text-gray-800'">{{ dueDateValue ? (dueDateValue | date:'dd/MM/yyyy') : '—' }}</div>
                        @if(dueDateValue) {
                        <div class="text-xs mt-0.5" [ngClass]="isOverdue ? 'text-red-500' : 'text-gray-400'">{{ dueRelative }}</div>
                        }
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-3 col-span-2 sm:col-span-1">
                        <div class="text-xs text-gray-500 font-semibold mb-1">Estimated</div>
                        <div class="text-sm font-medium text-gray-800">{{ taskData.estimatedTime ? taskData.estimatedTime + 'h' : '—' }}</div>
                    </div>
                </div>
            </div>
        </div>

        <!-- ── Right rail: conversation and change history ── -->
        <section class="w-[26rem] shrink-0 flex flex-col min-h-0 bg-white">
            <div class="flex items-center gap-1 px-4 pt-3 border-b border-gray-100 shrink-0">
                <button type="button" (click)="sidePanel = 'conversation'"
                    class="px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer"
                    [ngClass]="sidePanel === 'conversation' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'">
                    Conversation
                </button>
                <button type="button" (click)="sidePanel = 'history'"
                    class="px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors cursor-pointer"
                    [ngClass]="sidePanel === 'history' ? 'border-blue-600 text-blue-700' : 'border-transparent text-gray-500 hover:text-gray-700'">
                    History @if(auditEntries.length) { <span class="text-xs font-normal text-gray-400">({{ auditEntries.length }})</span> }
                </button>
            </div>

            <div class="flex-1 min-h-0 flex flex-col p-4">
                @if(sidePanel === 'conversation') {
                <app-comments [taskId]="taskToEdit!.id!" [projectId]="currentProjectId" [employees]="employees"></app-comments>
                } @else {
                <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar -mr-2 pr-2">
                    @if(loadingAudit) {
                    <div class="flex items-center justify-center py-8 text-sm text-gray-400">
                        <svg class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                        </svg>
                        Loading history...
                    </div>
                    } @else {
                    <ul class="space-y-4">
                        @for(entry of auditEntries; track entry.id) {
                        <li class="flex gap-3">
                            <div class="w-8 h-8 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center text-gray-600 text-[10px] font-bold shrink-0 mt-0.5">
                                {{ auditAuthorInitials(entry.changedBy) }}
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="flex items-baseline gap-2 mb-0.5 flex-wrap">
                                    <span class="text-sm font-semibold text-gray-800">{{ auditAuthorName(entry.changedBy) }}</span>
                                    <span class="text-[11px] text-gray-400">{{ entry.changedAt | date:'dd MMM, HH:mm' }}</span>
                                </div>
                                @if(entry.action === 'CREATED') {
                                <p class="text-sm text-gray-600">created this task</p>
                                } @else {
                                <p class="text-sm text-gray-600">
                                    changed <span class="font-medium text-gray-800">{{ auditFieldLabel(entry.field) }}</span>
                                </p>
                                <div class="mt-1 flex items-center gap-2 text-xs flex-wrap">
                                    <span class="px-1.5 py-0.5 rounded bg-red-50 text-red-700 line-through break-all">{{ auditValueLabel(entry.field, entry.oldValue) }}</span>
                                    <svg class="w-3 h-3 text-gray-300 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 5l7 7m0 0l-7 7m7-7H3" /></svg>
                                    <span class="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 break-all">{{ auditValueLabel(entry.field, entry.newValue) }}</span>
                                </div>
                                }
                            </div>
                        </li>
                        }
                        @empty {
                        <li class="flex flex-col items-center justify-center text-center py-10 px-4">
                            <div class="w-14 h-14 rounded-full bg-gray-50 flex items-center justify-center mb-3">
                                <svg class="w-7 h-7 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                            </div>
                            <p class="text-sm font-medium text-gray-600">No changes recorded</p>
                            <p class="text-xs text-gray-400 mt-1">Edits to this task will show up here</p>
                        </li>
                        }
                    </ul>
                    }
                </div>
                }
            </div>
        </section>
    </div>

    } @else {
    <!-- ============================ EDIT MODE (form) ============================ -->
    <!-- Header -->
    <div class="px-6 py-4 border-b border-gray-100 flex justify-between items-center shrink-0">
        <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shrink-0 shadow-sm">
                <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
            </div>
            <div>
                <h2 class="font-bold text-lg text-gray-900 leading-tight">
                    @if(taskToEdit) { Edit Task } @else { New Task }
                </h2>
                <p class="text-xs text-gray-400">@if(taskToEdit) { Update the task details } @else { Fill in the details below }</p>
            </div>
        </div>
        <!-- onCancel(), not close: leaving an edit on an existing task goes back to
             that task, not out to the project's phase list. -->
        <button (click)="onCancel()" class="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
        </button>
    </div>

    <form (ngSubmit)="onSubmit()" #taskForm="ngForm" class="flex flex-col flex-1 min-h-0">
        <div class="px-6 py-5 overflow-y-auto custom-scrollbar flex-1 space-y-6 bg-gray-50/40">

            <!-- Section: Details -->
            <div class="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    Details
                </div>

                <!-- Task Name -->
                <div class="flex flex-col">
                    <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide" for="nameTask">Task Name <span class="text-red-500">*</span></label>
                    <input type="text" id="nameTask" name="nameTask" required [(ngModel)]="taskData.nameTask"
                        class="border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none placeholder-gray-400 transition" placeholder="Enter task name" />
                </div>

                <!-- Description -->
                <div class="flex flex-col">
                    <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide" for="content">Description</label>
                    <app-rich-text-editor
                        id="content"
                        name="content"
                        [(ngModel)]="taskData.content"
                        placeholder="Describe the task..."
                        heightClass="min-h-[120px]">
                    </app-rich-text-editor>
                </div>

                <!-- Checklist — available while creating, not only once the task exists.
                     Drafted items live in memory and are written right after the task. -->
                <div class="flex flex-col">
                    <div class="flex items-center justify-between mb-1.5 relative">
                        <label class="text-xs text-gray-500 font-semibold uppercase tracking-wide">
                            Checklist
                            @if(checklist.length) {
                            <span class="text-blue-600 normal-case">({{ checklistDoneCount }}/{{ checklist.length }})</span>
                            }
                        </label>
                        <div class="flex items-center gap-3 shrink-0">
                            <button type="button" (click)="openTemplateMenu()"
                                class="inline-flex items-center gap-1 text-xs font-medium text-gray-500 hover:text-blue-600 cursor-pointer">
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" /></svg>
                                Templates
                            </button>
                            @if(!showChecklistAdd) {
                            <button type="button" (click)="toggleChecklistAdd()"
                                class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer">
                                <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                                Add item
                            </button>
                            }
                        </div>

                        @if(showTemplateMenu) {
                        <div class="absolute right-0 top-full mt-2 w-72 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
                            <div class="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-gray-400 border-b border-gray-100">
                                Apply a template
                            </div>
                            <div class="max-h-52 overflow-y-auto custom-scrollbar">
                                @for(tpl of checklistTemplates; track tpl.id) {
                                <div (click)="applyTemplate(tpl)"
                                    class="group flex items-center gap-2 px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-50 last:border-0">
                                    <div class="min-w-0 flex-1">
                                        <div class="text-sm text-gray-800 truncate">{{ tpl.name }}</div>
                                        <div class="text-[11px] text-gray-400">{{ tpl.items.length }} items</div>
                                    </div>
                                    <button type="button" (click)="deleteTemplate(tpl, $event)" title="Delete template"
                                        class="opacity-0 group-hover:opacity-100 p-1 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer shrink-0">
                                        <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                    </button>
                                </div>
                                }
                                @empty {
                                <p class="px-3 py-4 text-xs text-gray-400 text-center">No template yet</p>
                                }
                            </div>
                            <div class="border-t border-gray-100 bg-gray-50/70 p-2">
                                @if(newTemplateName === null) {
                                <button type="button" (click)="startSavingTemplate()" [disabled]="!checklist.length"
                                    class="w-full inline-flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold text-blue-600 hover:bg-blue-50 rounded-md disabled:opacity-40 disabled:cursor-not-allowed cursor-pointer">
                                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" /></svg>
                                    Save this checklist as a template
                                </button>
                                } @else {
                                <div class="flex items-center gap-1.5">
                                    <input type="text" [(ngModel)]="newTemplateName" [ngModelOptions]="{ standalone: true }"
                                        placeholder="Template name" (keydown.enter)="$event.preventDefault(); saveChecklistAsTemplate()"
                                        class="flex-1 min-w-0 px-2 py-1.5 text-xs border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400/50" />
                                    <button type="button" (click)="saveChecklistAsTemplate()"
                                        [disabled]="!newTemplateName || savingTemplate"
                                        class="px-2.5 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer shrink-0">Save</button>
                                </div>
                                }
                            </div>
                        </div>
                        }
                    </div>

                    @if(checklist.length) {
                    <div class="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden mb-2">
                        <div class="h-full bg-emerald-500 transition-all duration-300" [style.width.%]="checklistPercent"></div>
                    </div>
                    }

                    @if(showChecklistAdd) {
                    <div class="flex items-center gap-2 mb-2">
                        <input type="text" [(ngModel)]="newChecklistLabel" [ngModelOptions]="{ standalone: true }"
                            placeholder="What needs doing?" (keydown.enter)="$event.preventDefault(); addChecklistItem()"
                            (keydown.escape)="toggleChecklistAdd()"
                            class="flex-1 min-w-0 px-2.5 py-1.5 text-sm border border-gray-300 rounded-md outline-none focus:ring-2 focus:ring-blue-400/50" />
                        <button type="button" (click)="addChecklistItem()" [disabled]="!newChecklistLabel.trim()"
                            class="px-2.5 py-1.5 bg-blue-600 text-white text-xs font-semibold rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer shrink-0">Add</button>
                    </div>
                    }

                    @if(checklistError) {
                    <div class="flex items-start gap-2 mb-2 p-2.5 rounded-lg border border-amber-200 bg-amber-50">
                        <svg class="w-4 h-4 text-amber-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                        </svg>
                        <p class="text-xs text-amber-800 min-w-0 break-words flex-1">{{ checklistError }}</p>
                        <button type="button" (click)="checklistService.clearError()"
                            class="p-0.5 text-amber-600 hover:bg-amber-100 rounded cursor-pointer shrink-0">
                            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                        </button>
                    </div>
                    }

                    @if(checklist.length) {
                    <ul class="space-y-1">
                        @for(item of checklist; track item.id) {
                        <li class="group flex items-start gap-2.5 py-1">
                            <button type="button" (click)="toggleChecklistItem(item)"
                                class="shrink-0 mt-0.5 w-4 h-4 rounded border-2 flex items-center justify-center transition-colors cursor-pointer"
                                [class.bg-emerald-500]="item.done" [class.border-emerald-500]="item.done"
                                [class.border-gray-300]="!item.done" [class.bg-white]="!item.done">
                                @if(item.done) {
                                <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" /></svg>
                                }
                            </button>
                            <span class="flex-1 min-w-0 text-sm break-words" [class.line-through]="item.done"
                                [class.text-gray-400]="item.done" [class.text-gray-700]="!item.done">{{ item.label }}</span>
                            <button type="button" (click)="removeChecklistItem(item)"
                                class="opacity-0 group-hover:opacity-100 p-0.5 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer shrink-0">
                                <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </li>
                        }
                    </ul>
                    } @else if(!showChecklistAdd) {
                    <p class="text-xs text-gray-400 italic">No checklist yet — add an item or apply a template.</p>
                    }
                </div>

                <!-- Links -->
                <div class="flex flex-col">
                    <div class="flex items-center justify-between mb-1.5">
                        <label class="text-xs text-gray-500 font-semibold uppercase tracking-wide">Links</label>
                        @if(!showAddLinkForm) {
                        <button type="button" (click)="toggleAddLink()"
                            class="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-700 cursor-pointer">
                            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
                            Add link
                        </button>
                        }
                    </div>

                    @if(showAddLinkForm) {
                    <div class="mb-2 p-2.5 bg-blue-50 rounded-lg border border-blue-200 flex gap-2 items-end flex-wrap">
                        <select name="newLinkType" [(ngModel)]="newLinkType" [ngModelOptions]="{ standalone: true }" (ngModelChange)="onLinkTypeChange()"
                            class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none bg-white cursor-pointer">
                            <option value="Drive">Drive</option>
                            <option value="Other">Other</option>
                        </select>
                        <input type="text" name="newLinkName" [(ngModel)]="newLinkName" [ngModelOptions]="{ standalone: true }"
                            [readonly]="newLinkType === 'Drive'" placeholder="Name"
                            class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none flex-1 min-w-[80px]"
                            [class.bg-gray-100]="newLinkType === 'Drive'">
                        <input type="url" name="newLinkUrl" [(ngModel)]="newLinkUrl" [ngModelOptions]="{ standalone: true }"
                            (keydown.enter)="$event.preventDefault(); addLink()" (keydown.escape)="cancelAddLink()"
                            placeholder="https://drive.google.com/..."
                            class="px-2 py-1 text-xs border border-gray-300 rounded-md outline-none flex-1 min-w-[140px]">
                        <button type="button" (click)="addLink()" [disabled]="!newLinkUrl || !newLinkName"
                            class="px-2.5 py-1 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 disabled:opacity-50 cursor-pointer">Add</button>
                        <button type="button" (click)="cancelAddLink()"
                            class="px-2 py-1 bg-gray-200 text-gray-600 text-xs rounded-md hover:bg-gray-300 cursor-pointer">&#10005;</button>
                    </div>
                    }

                    @if(taskData.links.length > 0) {
                    <div class="flex flex-wrap gap-1.5">
                        @for(link of taskData.links; track $index) {
                        <div class="group inline-flex items-center gap-1.5 px-2.5 py-1 bg-gray-50 border border-gray-200 rounded-md text-xs">
                            @if(isDriveLink(link)) {
                            <svg class="w-3 h-3 shrink-0 text-gray-500" viewBox="0 0 24 24" fill="currentColor"><path d="M7.71 3.5L1.15 15l3.43 5.5L11.14 9zM9.4 15l-3.28 5.5h11.76L21.16 15zm12.02-1.5L15.28 3.5H8.42l6.14 10z" /></svg>
                            }
                            <a [href]="link.url" (click)="openExternalLink(link.url, $event)" target="_blank" rel="noopener noreferrer"
                                class="text-blue-600 hover:underline truncate max-w-[160px] cursor-pointer">{{ link.name }}</a>
                            <button type="button" (click)="removeLink($index)"
                                class="opacity-0 group-hover:opacity-100 p-0.5 text-red-500 hover:bg-red-100 rounded transition-all cursor-pointer">
                                <svg class="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                            </button>
                        </div>
                        }
                    </div>
                    } @else if(!showAddLinkForm) {
                    <p class="text-xs text-gray-400 italic">No link yet — add a Drive folder or any URL.</p>
                    }
                </div>
            </div>

            <!-- Section: Classification -->
            <div class="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5a1.99 1.99 0 011.414.586l7 7a2 2 0 010 2.828l-5 5a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z" /></svg>
                    Classification
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    @if(taskToEdit) {
                    <div class="flex flex-col">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide">Status</label>
                        <div class="grid grid-cols-3 gap-1.5">
                            <button type="button" (click)="taskData.status = 'NOTBEGIN'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.status === 'NOTBEGIN' ? 'bg-slate-50 border-slate-400 text-slate-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-slate-400"></span> New
                            </button>
                            <button type="button" (click)="taskData.status = 'INPROGRESS'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.status === 'INPROGRESS' ? 'bg-blue-50 border-blue-400 text-blue-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-blue-500"></span> In Progress
                            </button>
                            <button type="button" (click)="taskData.status = 'DONE'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.status === 'DONE' ? 'bg-emerald-50 border-emerald-400 text-emerald-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Done
                            </button>
                        </div>
                    </div>
                    }

                    @if(taskToEdit && projectPhases.length > 1) {
                    <!-- Move the task to another phase of the same project. Edit mode only:
                         on create the phase comes from the caller and is fixed. -->
                    <div class="flex flex-col sm:col-span-2">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide" for="taskPhase">Phase</label>
                        <select id="taskPhase" name="projectStageId" [(ngModel)]="taskData.projectStageId"
                            class="border border-gray-300 rounded-lg px-3.5 py-2.5 text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition">
                            @for(phase of projectPhases; track phase.projectStageId){
                            <option [value]="phase.projectStageId">{{ phase.stageName }}</option>
                            }
                        </select>
                        @if(isMovingPhase) {
                        <p class="mt-1.5 text-xs text-amber-700 inline-flex items-start gap-1.5">
                            <svg class="w-3.5 h-3.5 shrink-0 mt-px" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                    d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                            </svg>
                            Saving will move this task out of “{{ auditValueLabel('projectStageId', taskToEdit!.projectStageId) }}”.
                        </p>
                        }
                    </div>
                    }

                    <div class="flex flex-col" [class.sm:col-span-2]="!taskToEdit">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide">Priority</label>
                        <div class="grid grid-cols-3 gap-1.5">
                            <button type="button" (click)="taskData.priority = 'LOW'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.priority === 'LOW' ? 'bg-gray-50 border-gray-400 text-gray-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-gray-400"></span> Low
                            </button>
                            <button type="button" (click)="taskData.priority = 'MEDIUM'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.priority === 'MEDIUM' ? 'bg-amber-50 border-amber-400 text-amber-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-amber-500"></span> Medium
                            </button>
                            <button type="button" (click)="taskData.priority = 'HIGH'"
                                class="flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold border transition-all cursor-pointer"
                                [ngClass]="taskData.priority === 'HIGH' ? 'bg-red-50 border-red-400 text-red-700' : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'">
                                <span class="w-2 h-2 rounded-full bg-red-500"></span> High
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Section: Schedule -->
            <div class="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                    Schedule
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-5">
                    <div class="flex flex-col">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide">Start Date</label>
                        <kendo-datepicker
                            [value]="startDateValue"
                            (valueChange)="onStartDateChange($event)"
                            [format]="'dd/MM/yyyy'"
                            [navigation]="true"
                            [popupSettings]="{ popupClass: 'kendo-datepicker-large' }"
                            placeholder="dd/mm/yyyy"
                            class="w-full h-[42px]">
                        </kendo-datepicker>
                    </div>

                    <div class="flex flex-col">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide">Due Date
                            @if(!taskToEdit) { <span class="text-red-500">*</span> }
                        </label>
                        <kendo-datepicker
                            [value]="dueDateValue"
                            (valueChange)="onDueDateChange($event)"
                            [format]="'dd/MM/yyyy'"
                            [navigation]="true"
                            [popupSettings]="{ popupClass: 'kendo-datepicker-large' }"
                            placeholder="dd/mm/yyyy"
                            class="w-full h-[42px] rounded"
                            [class.ring-2]="isDueDateMissing"
                            [class.ring-red-300]="isDueDateMissing">
                        </kendo-datepicker>
                        @if(isDueDateMissing) {
                        <span class="text-xs text-red-500 mt-1">Pick a due date</span>
                        }
                    </div>

                    <div class="flex flex-col">
                        <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide" for="estimatedTime">Estimated Time</label>
                        <div class="relative">
                            <input type="text" id="estimatedTime" inputmode="decimal" pattern="[0-9]*[.,]?[0-9]*" [(ngModel)]="taskData.estimatedTime" name="estimatedTime"
                                class="w-full border border-gray-300 rounded-lg pl-3.5 pr-8 py-2.5 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none bg-white transition" placeholder="0" />
                            <span class="absolute inset-y-0 right-3 flex items-center text-sm text-gray-400 pointer-events-none">h</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Section: Team -->
            <div class="rounded-xl border border-gray-200 bg-white p-4 space-y-4">
                <div class="flex items-center gap-1.5 text-xs text-gray-400 font-bold uppercase tracking-wider">
                    <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4z" /></svg>
                    Team
                </div>

                <!-- Assign to -->
                <div class="flex flex-col">
                    <label class="text-xs text-gray-500 font-semibold mb-1.5 uppercase tracking-wide">
                        Assign to @if(taskData.employeeId.length) { <span class="text-blue-600">({{ taskData.employeeId.length }})</span> }
                    </label>
                    <div class="border border-gray-200 rounded-xl bg-gray-50/60 overflow-hidden">
                        @if(loadingUsers) {
                        <div class="flex items-center justify-center p-6 text-gray-500 text-sm gap-2">
                            <svg class="animate-spin h-5 w-5 text-blue-600" viewBox="0 0 24 24" fill="none">
                                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Loading team members...
                        </div>
                        } @else if(selectableEmployees.length === 0) {
                        <div class="flex items-center justify-center p-6 text-gray-400 text-sm">No team members available</div>
                        } @else {
                        @let assigned = getAssignedEmployees();
                        @let filtered = getFilteredEmployees();

                        <!-- Selected chips -->
                        @if(assigned.length > 0) {
                        <div class="flex flex-wrap gap-1.5 p-2 border-b border-gray-100 bg-white">
                            @for(emp of assigned; track emp.id) {
                            <span class="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-0.5 rounded-full bg-blue-50 border border-blue-200 text-blue-800">
                                <span class="w-5 h-5 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[8px] font-bold shrink-0">{{ emp.firstName?.charAt(0) || '?' }}{{ emp.name?.charAt(0) || '' }}</span>
                                <span class="text-xs font-medium">{{ emp.firstName }} {{ emp.name }}</span>
                                @if(emp.isArchived) {
                                <span class="inline-flex items-center px-1.5 py-0.5 rounded-md text-[9px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 shrink-0">Archived</span>
                                }
                                <button type="button" (click)="toggleEmployeeSelection(emp.id)" class="text-blue-400 hover:text-red-500 cursor-pointer" title="Remove">
                                    <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
                                </button>
                            </span>
                            }
                        </div>
                        }

                        <!-- Search -->
                        <div class="p-2 border-b border-gray-100 bg-white">
                            <div class="relative">
                                <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-4.35-4.35M17 11a6 6 0 11-12 0 6 6 0 0112 0z" />
                                </svg>
                                <input type="text" [(ngModel)]="assigneeSearch" [ngModelOptions]="{ standalone: true }"
                                    (keydown.enter)="$event.preventDefault()" placeholder="Search members..."
                                    class="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-gray-200 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition" />
                            </div>
                        </div>

                        <!-- List -->
                        <div class="p-2 overflow-y-auto custom-scrollbar max-h-[220px] space-y-1">
                            @for(emp of filtered; track emp.id) {
                            @let selected = isEmployeeSelected(emp.id);
                            <button type="button" (click)="toggleEmployeeSelection(emp.id)"
                                class="w-full flex items-center gap-3 p-2 rounded-lg border transition-all cursor-pointer"
                                [ngClass]="selected ? 'bg-blue-50 border-blue-200' : 'bg-transparent border-transparent hover:bg-white hover:border-gray-200'">
                                <span class="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[10px] font-bold shadow-sm shrink-0">
                                    {{ emp.firstName?.charAt(0) || '?' }}{{ emp.name?.charAt(0) || '' }}
                                </span>
                                <span class="flex-1 min-w-0 text-left">
                                    <span class="flex items-center gap-2 min-w-0">
                                        <span class="block text-sm font-medium truncate" [ngClass]="selected ? 'text-blue-800' : 'text-gray-700'">{{ emp.firstName }} {{ emp.name }}</span>
                                        @if(emp.isArchived) {
                                        <span class="inline-flex items-center px-2 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-amber-100 text-amber-700 border border-amber-200 shrink-0">Archived</span>
                                        }
                                    </span>
                                    @if(emp.email) { <span class="block text-xs text-gray-400 truncate">{{ emp.email }}</span> }
                                </span>
                                <span class="w-5 h-5 rounded-full flex items-center justify-center shrink-0 border-2 transition-colors"
                                    [ngClass]="selected ? 'bg-blue-600 border-blue-600' : 'bg-white border-gray-300'">
                                    @if(selected) {
                                    <svg class="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                    }
                                </span>
                            </button>
                            }
                            @empty {
                            <div class="text-center text-sm text-gray-400 py-6">No members found</div>
                            }
                        </div>
                        }
                    </div>
                </div>
            </div>
        </div>

        <!-- Footer / Actions -->
        <div class="flex justify-between items-center px-6 py-3.5 border-t border-gray-100 shrink-0 bg-gray-50/50">
            <div>
                @if(taskToEdit && canUserAccessTask()) {
                <button type="button" (click)="deleteTask()"
                    class="inline-flex items-center gap-2 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors cursor-pointer">
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                </button>
                }
            </div>
            <div class="flex gap-2">
                <button type="button" (click)="onCancel()"
                    class="px-4 py-2.5 text-gray-700 text-sm font-medium hover:bg-gray-100 rounded-lg transition-colors cursor-pointer">
                    Cancel
                </button>
                <button type="submit" [disabled]="!taskForm.form.valid || isDueDateMissing || isSubmitting"
                    class="bg-blue-600 text-white text-sm font-semibold py-2.5 px-5 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm cursor-pointer flex items-center gap-2">
                    @if(isSubmitting) {
                    <svg class="animate-spin h-4 w-4 text-white" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Saving...
                    } @else {
                    @if(taskToEdit) { Update Task } @else { Create Task }
                    }
                </button>
            </div>
        </div>
    </form>
    }
</div>

@if(showConfirm){
  <app-confirm-modal
    [message]="confirmMessage"
    (close)="handleConfirm($event)">
  </app-confirm-modal>
}
